<template>
  <AppView>
    <TopNav
      title="New task"
      title-class="font-title lg-app:ml-body"
      center-title
      type="back"
      slot="top-app-view"
      :prev-route="prevRoute"
    />
    <div class="mx-body tw-mb-8">
      <div v-if="errors.form">
        <p class="tw-text-center tw-text-base opacity-78 tw-my-20">{{ errors.form }}</p>
      </div>
      <div v-else-if="loading">
        <p class="tw-text-center tw-text-xs opacity-54 tw-my-20">Loading...</p>
      </div>

      <div v-else-if="taskCreatedSuccess" class="tw-text-center">
        <p class="tw-text-center tw-text-base opacity-78 tw-mt-20">Task Created successfully for {{ menteeName }}!</p>
        <BaseButton
          @click="resetForm(); taskCreatedSuccess = false;"
          text="Create another task"
          class="tw-mt-6 tw-mx-6"
        />
        <BaseButton
          :to="{ name: 'mentorMenteeTasks', params: { id: id }}"
          text="View mentee's tasks"
          class="tw-mt-6 tw-mx-auto"
        />
      </div>

      <form
        method="POST"
        enctype="multipart/form-data"
        ref="create-task-form"
        class="tw-pb-16 tw-pt-8 tw-max-w-md tw-mx-auto"
        :key="formKey"
        v-else
      >
        <div
          class="tw-inline-flex tw-items-center tw-p-2 tw-mb-6 tw-bg-gray-300 tw-rounded-full"
          :title="`You are creating a task for ${menteeName}`"
        >
          <UserPhoto
            :photo="mentee.user.photo"
            photo-size="tw-h-6 tw-w-6"
            class="tw-inline-block"
          />
          <h5 class="tw-text-sm font-title leading-17 tw-ml-2">{{ menteeName }}</h5>
        </div>

        <p class="tw-text-xs leading-17 opacity-54">Which goal is this task for?</p>
        <v-select
          class="my-select transition tw-text-base tw-leading-normal tw-py-2 tw-placeholder-black tw-w-full tw-border-b tw-border-black tw-bg-transparent tw-mb-6 app-bg tw-shadow-none"
          placeholder="Goal"
          v-model="taskGoalId"
          label="goal"
          :value="taskGoalId"
          :options="userGoals"
          :reduce="goal => goal.id"
          :multiple="false"
          :clearable="false"
          :filterable="true"
          :searchable="true"
          append-to-body
          :calculate-position="dropdownPosition"
        />

        <BaseInput
          type="text"
          placeholder="Key Objective"
          name="taskname"
          v-model="taskName"
          input-class="tw-text-base tw-leading-none tw-py-2 tw-placeholder-gray-500 tw-w-full tw-border-b tw-border-black tw-bg-transparent tw-mb-6"
        />
        <BaseTextarea
          name="taskdescription"
          rows="1"
          class="tw-text-base tw-leading-normal tw-py-2 tw-placeholder-black tw-w-full tw-border-b tw-border-black tw-bg-transparent tw-mb-6"
          style="background: transparent;"
          placeholder="Task description"
          :value="taskDescription"
          @input="(description) => taskDescription = description"
          v-model.lazy="taskDescription"
        />
        <p class="tw-text-xs leading-17 opacity-54">Resources</p>
        <div
          v-if="hasAttachedResources"
          class="tw-flex stacked-grid lg:stacked-grid--stack-two tw-flex-wrap tw-items-start tw-mt-4"
        >
          <div
            class="info-card tw-shadow-app tw-relative tw-cursor-pointer"
            v-for="resource in taskResources"
            :key="resource.id"
          >
            <h4 class="leading-19 tw-text-base tw-p-2 tw-break-words">{{ resource.name || resource.data }}</h4>
            <div class="tw-flex tw-items-end">
              <p class="tw-text-sm tw-leading-17 tw-font-bold tw-p-2 tw-mt-3 tw-uppercase info-card__action-info info-card__action-info--left">
                {{ resourceType(resource.file_type) }}
              </p>
              <button
                type="button"
                class="leading-14 tw-p-1 tw-ml-auto tw-mr-2 tw-mb-1 tw-border-box tw-inline-block tw-text-red-500 active:tw-bg-red-300 focus:tw-bg-red-300 tw-fill-current"
                @click.stop="removeResource(resource.id)"
              >
                <span class="tw-inline-block tw-uppercase tw-font-bold tw-text-xs tw-align-middle">Remove</span>
                <svg class="tw-w-6 tw-h-6 tw-ml-1 tw-inline-block" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px"
                    y="0px" viewBox="0 0 72 72" style="enable-background:new 0 0 72 72;" xml:space="preserve">
                  <path d="M63.1,15.2V64c0,4.4-3.6,8-8,8h-38c-4.4,0-8-3.6-8-8V15.2c0-1.1,0.8-1.9,1.9-1.9s1.9,0.8,1.9,1.9V64c0,2.3,1.9,4.2,4.2,4.2
                    h38c2.3,0,4.2-1.9,4.2-4.2V15.2c0-1.1,0.8-1.9,1.9-1.9S63.1,14.2,63.1,15.2z M66.3,4.9H38v-3C38,0.8,37.2,0,36.1,0
                    c-1.1,0-1.9,0.8-1.9,1.9v3H5.9C4.7,4.9,4,5.7,4,6.8s0.8,1.9,1.9,1.9h60.2C67.3,8.7,68,8,68,6.8S67.3,4.9,66.3,4.9z M24.7,58.9V17.1
                    c0-1.1-0.8-1.9-1.9-1.9s-1.9,0.8-1.9,1.9v41.8c0,1.1,0.8,1.9,1.9,1.9S24.7,60,24.7,58.9z M38,58.9V17.1c0-1.1-0.8-1.9-1.9-1.9
                    c-1.1,0-1.9,0.8-1.9,1.9v41.8c0,1.1,0.8,1.9,1.9,1.9C37.2,60.8,38,60,38,58.9z M51.3,58.9V17.1c0-1.1-0.8-1.9-1.9-1.9
                    s-1.9,0.8-1.9,1.9v41.8c0,1.1,0.8,1.9,1.9,1.9S51.3,60,51.3,58.9z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div
          v-else
          class="tw-py-10 tw-text-center"
        >
          <p class="tw-text-center tw-text-base opacity-78 tw-pb-4">Upload some resources for the task</p>

          <BaseButton
            text="Attach"
            type="button"
            @click.native.stop="attachResources"
          />

        </div>

        <p class="tw-text-xs leading-17 opacity-54 tw-mt-4"> {{ taskTimeInfo }} </p>
        <div class="tw-flex tw-flex-wrap tw-mb-2 min-h-60">
          <div class="tw-w-1/2" style="padding-right: 5px;">
            <p class="tw-text-xs leading-17 opacity-54">Start time</p>
            <p class="tw-text-base leading-17" v-if="taskStartTime">{{ taskStartTime | moment('Do, MMM YYYY') }}</p>
          </div>
          <div class="tw-w-1/2" style="padding-left: 5px;">
            <p class="tw-text-xs leading-17 opacity-54">End time</p>
            <p class="tw-text-base leading-17" v-if="taskEndTime">{{ taskEndTime | moment('Do, MMM YYYY') }}</p>
            <p class="tw-text-left tw-text-xs tw-text-red-400 opacity-78" v-if="errors.taskEndTime">{{ errors.taskEndTime }}</p>
          </div>
        </div>

        <div class="tw-w-full tw-mb-8">
            <!-- [TODO] ADD LIMITS
              :limits="{min: lastWeek(), max: threeMonthsLater()}" -->
          <FunctionalCalendar
            placeholder="Start time"
            ref="task-time-range"
            v-model="taskTimeData"
            :sunday-start="true"
            :date-format="'mm/dd/yyyy'"
            :is-date-range="true"
            :is-date-picker="false"
            :change-month-function="false"
            :change-year-function="false"
            :limits="{
              min: lastWeek(),
              max: threeMonthsLater()
            }"
            :day-names="['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']"
          />
        </div>

        <BaseButton
          :text="saveButtonText"
          style="margin-top: 80px;"
          class="tw-shadow-app tw-block tw-w-full tw-bg-black tw-border-transparent tw-text-white tw-py-3"
          :disabled="!canSave || saveDisabled"
          @click="onSubmit"
        />
      </form>
    </div>
  </AppView>
</template>

<script>
import { FunctionalCalendar } from 'vue-functional-calendar';
import BaseInput from '@/components/BaseInput.vue';
import form from '@/modules/formHelper';
import { apiStore, generateFormData } from '@/modules/apiHelper';
import { totalSumOfArray, toJsDate } from '@/modules/dataHelper';

export default {
  name: 'CreateTask',
  components: {
    BaseInput,
    FunctionalCalendar,
  },
  props: ['id', 'prevRoute'],
  data() {
    return {
      loading: true,
      formKey: 0,
      taskCreatedSuccess: false,
      taskName: undefined,
      taskDescription: undefined,
      taskResources: [],
      taskTimeData: {},
      taskGoalId: undefined,
      taskTimeInfo: undefined,
      userGoals: [],
      errors: {
        form: undefined,
        taskName: undefined,
        taskDescription: undefined,
        taskStartTime: undefined,
        taskEndTime: undefined,
        taskGoalId: undefined,
      },
      saveButtonText: 'SAVE',
      saveDisabled: false,
    };
  },
  watch: {
    taskName(newVal) {
      if (!form.isValidTextInput(newVal)) {
        this.errors.taskName = 'Task name is required';
      }
      this.errors.taskName = undefined;
    },
    taskDescription(newVal) {
      if (!form.isValidTextInput(newVal)) {
        this.errors.taskDescription = 'Task description is required';
      }
      this.errors.taskDescription = undefined;
    },
    // eslint-disable-next-line func-names
    taskEndTime() {
      if (!this.isValidTimeRange(true) && this.taskEndTime) {
        this.errors.taskEndTime = 'Select a valid date range';
      } else {
        this.errors.taskEndTime = undefined;
      }
    },
  },
  computed: {
    menteeName() {
      if (typeof this.mentee !== 'object') {
        return '';
      }
      return `${this.mentee.user.name} ${this.mentee.user.last_name}`;
    },
    mentee() {
      return this.$store.getters.getMentee(this.id);
    },
    hasAttachedResources() {
      return this.taskResources.length > 0;
    },
    canSave() {
      if (
        form.isValidTextInput(this.taskName)
        && form.isValidTextInput(this.taskDescription)
        && this.isValidTimeRange(true)
        && !!this.taskGoalId
      ) {
        return true;
      }
      return false;
    },
    taskStartTime() {
      if (this.taskTimeData.dateRange) {
        if (this.taskTimeData.dateRange.start.date) {
          return toJsDate(this.taskTimeData.dateRange.start.date, true);
        }
      }
      return undefined;
    },
    taskEndTime() {
      if (this.taskTimeData.dateRange) {
        if (this.taskTimeData.dateRange.end.date) {
          return toJsDate(this.taskTimeData.dateRange.end.date, true);
        }
      }
      return undefined;
    },
  },
  methods: {
    lastWeek() {
      return this.$moment(new Date()).subtract(6, 'days').format('YYYY-MM-DD');
    },
    threeMonthsLater() {
      return this.$moment(new Date()).add(3, 'months').format('YYYY-MM-DD');
    },
    async attachResources() {
      await this.$store.dispatch('saveCreateTaskDraft', [this.id, [this.taskName, this.taskDescription, this.taskGoalId]]);
      await this.$nextTick();

      this.$router.push({
        name: 'addResourcesToTask',
        params: {
          id: this.id,
        },
      });
    },
    async removeResource(resourceId) {
      const index = this.taskResources.findIndex(r => r.id === resourceId);
      if (index >= 0) {
        this.taskResources.splice(index, 1);

        if (this.taskResources.length > 0) {
          await apiStore.setItem(`CreateTaskRefResources/${this.id}`, this.taskResources);
        } else {
          await apiStore.removeItem(`CreateTaskRefResources/${this.id}`);
        }
      }
    },
    isValidTimeRange(isStrict = false) {
      const validations = [false, false];

      if (this.taskStartTime) {
        validations[0] = true;
      }

      if (this.taskEndTime) {
        validations[1] = true;
      }

      if (this.taskStartTime && this.taskEndTime) {
        if (this.taskTimeIsValid(this.taskStartTime, this.taskEndTime)) {
          return isStrict ? true : validations;
        }

        return isStrict ? false : validations;
      }

      return isStrict ? false : validations;
    },
    // [TODO] How to reset taskTimeData
    async resetForm() {
      // this.taskGoalId = undefined;
      // this.taskTimeData = Object.assign({}, {});
      this.taskName = undefined;
      this.taskDescription = undefined;
      this.taskResources = [];
      await this.$store.dispatch('clearReferencedItem', `draftTask/${this.id}`);
      await this.$store.dispatch('clearReferencedItem', `CreateTaskRefResources/${this.id}`);

      this.formKey += 1;
    },
    resetFormErrors() {
      this.errors.form = undefined;
      this.errors.taskName = undefined;
      this.errors.taskDescription = undefined;
      this.errors.taskStartTime = undefined;
      this.errors.taskEndTime = undefined;
      this.errors.taskGoalId = undefined;
    },
    async onSubmit() {
      if (!this.canSave) {
        this.$toasted.info('Make sure you have set all the inputs, including the mentee\'s goal',
          {
            duration: 4000,
            position: 'bottom-right',
          });
        return false;
      }

      this.saveButtonText = 'working...';
      this.saveDisabled = true;

      const now = new Date();
      const formData = generateFormData({
        sub_goalid: this.taskGoalId,
        type: 1, // NOT SURE THE CORRECT TYPE
        userid: this.id,
        mentorid: this.$store.state.User.id,
        task_name: this.taskName,
        desc: this.taskDescription,
        start_tym: this.$moment(this.taskStartTime).add(/* add now's seconds */ now.getHours(), 'h').format('YYYY-MM-DD HH:mm:ss'),
        end_tym: this.$moment(this.taskEndTime).add(/* add now's seconds */ now.getHours(), 'h').format('YYYY-MM-DD HH:mm:ss'),
      });

      const [response, resourcesResponse, progress] = await this.$store.dispatch('createTask', [this.id, formData, this.taskResources])
        .catch(() => {
          this.$toasted.global.appError({
            errorMessage: 'Error in creating task. Please try again later',
            duration: 5000,
            position: 'top-right',
          });
          return [false, false, 100];
        });

      if (!response || !resourcesResponse) return false;

      // wait for upload to complete to show success message
      // eslint-disable-next-line no-unused-vars
      const result = await new Promise(async (resolve, reject) => {
        if (progress.length === 0) {
          this.saveButtonText = 'working... (100%)';
          await resourcesResponse;
          resolve();
          return;
        }
        const $this = this;
        // eslint-disable-next-line prefer-arrow-callback
        setTimeout(async function again() {
          const totalPercentage = Math.round((totalSumOfArray(progress) / progress.length) || 0);
          $this.saveButtonText = `working... (${totalPercentage}%)`;
          if (totalPercentage < 100) {
            setTimeout(again, 100);
          } else {
            await resourcesResponse;
            resolve();
          }
        }, 500);
      }).then(async () => {
        await this.$nextTick();
        this.$toasted.success(`Task ${this.taskName} created`, {
          duration: 2000,
          position: 'top-right',
        });

        await this.$nextTick();
        this.taskCreatedSuccess = true;

        this.resetForm();
        this.resetFormErrors();

        await this.$nextTick();
        this.saveButtonText = 'SAVE';
        this.saveDisabled = false;

        return response;
      });

      return result;
    },
  },
  async created() {
    const isMatched = await this.assertMatch(this.id);
    if (!isMatched && this.isMentor) return false;

    this.$store.commit('isFlatView', true);
    this.errors.form = undefined;
    this.taskGoalId = 'loading...'; // for the select value to show a loading state
    let taskGoalId;
    this.loading = false;

    // get resources to share.
    this.taskResources = await this.$store.dispatch('getCreateTaskReferencedItem', `CreateTaskRefResources/${this.id}`) || [];
    [
      this.taskName,
      this.taskDescription,
      taskGoalId,
    ] = await this.$store.dispatch('getTaskDraft', this.id);

    taskGoalId = this.$route.query.goal || taskGoalId;

    return this.$store.dispatch('getMenteeGoals', [this.id, true])
      .then(async (res) => {
        if (res.length === 0) {
          this.errors.form = 'Error in getting mentee\'s goals';

          this.$toasted.global.appError({
            errorMessage: this.errors.form,
            duration: 4000,
            position: 'top-right',
          });
        } else {
          this.userGoals = res;

          // preselect the goal to set the task
          if (this.userGoals.find(goal => goal.id === taskGoalId)) {
            this.taskGoalId = taskGoalId || undefined;
          } else {
            this.taskGoalId = undefined;
          }
        }
      });
  },
};
</script>

<style type="css" lang="postcss">
.vfc-styles-conditional-class .vfc-main-container .vfc-calendars-container .vfc-calendars .vfc-calendar div.vfc-content .vfc-week div.vfc-day span.vfc-span-day.vfc-today {
  background-color: var(--black-87) !important;
}

.vfc-styles-conditional-class .vfc-main-container {
  font-family: 'Nunito' !important;
  @apply shadow-app-lg;
}
</style>
